import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import usePageTitle from '../../hooks/usePageTitle';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLaptop, faMobileScreen, faShield, faRotateRight, faXmark } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import i18n from '../../i18n';
import { getSessions, revokeSession, revokeOtherSessions } from '../../api/sessions';
import DashboardShell from '../../components/Dashboard/DashboardShell';

const DEVICE_ICON = { Mobile: faMobileScreen, Desktop: faLaptop };

function timeAgo(date) {
  const seconds = Math.floor((Date.now() - new Date(date)) / 1000);
  if (seconds < 60) return i18n.t('Just now');
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}${i18n.t('m ago')}`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}${i18n.t('h ago')}`;
  const days = Math.floor(hours / 24);
  return `${days}${i18n.t('d ago')}`;
}

function SessionCard({ session, onRevoke, revoking }) {
  const { t } = useTranslation();
  const icon = DEVICE_ICON[session.device] || faLaptop;

  return (
    <div className={`tw-flex tw-items-center tw-gap-4 tw-p-4 tw-rounded-xl tw-border tw-transition-all ${
      session.isCurrent
        ? 'tw-border-[#FF43A7]/40 tw-bg-[#FF43A7]/5'
        : 'tw-border-white/10 tw-bg-white/5 hover:tw-bg-white/8'
    }`}>
      <div className={`tw-flex tw-h-11 tw-w-11 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-xl ${
        session.isCurrent ? 'tw-bg-[#FF43A7]/20' : 'tw-bg-[#712CB0]/20'
      }`}>
        <FontAwesomeIcon
          icon={icon}
          className={`tw-text-lg ${session.isCurrent ? 'tw-text-[#FF43A7]' : 'tw-text-[#712CB0]'}`}
        />
      </div>

      <div className="tw-flex-1 tw-min-w-0">
        <div className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
          <span className="tw-text-sm tw-font-semibold tw-text-white">
            {session.browser} {t("on")} {session.os}
          </span>
          {session.isCurrent && (
            <span className="tw-rounded-full tw-bg-[#FF43A7]/20 tw-border tw-border-[#FF43A7]/40 tw-px-2 tw-py-0.5 tw-text-[10px] tw-font-bold tw-text-[#FF43A7] tw-uppercase tw-tracking-wide">
              {t("This device")}
            </span>
          )}
        </div>
        <div className="tw-flex tw-gap-3 tw-mt-1 tw-flex-wrap">
          <span className="tw-text-xs tw-text-white/40">{session.ip || t("IP unknown")}</span>
          <span className="tw-text-xs tw-text-white/40">{t("Active")} {timeAgo(session.lastActive)}</span>
        </div>
      </div>

      {!session.isCurrent && (
        <button
          onClick={() => onRevoke(session._id)}
          disabled={revoking === session._id}
          className="tw-flex tw-h-9 tw-w-9 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-white/20 tw-bg-transparent hover:tw-border-red-500/60 hover:tw-bg-red-500/10 hover:tw-text-red-400 tw-text-white/40 tw-transition-all tw-cursor-pointer disabled:tw-opacity-40"
          title={t("Revoke session")}
        >
          {revoking === session._id
            ? <FontAwesomeIcon icon={faRotateRight} className="tw-animate-spin tw-text-xs" />
            : <FontAwesomeIcon icon={faXmark} className="tw-text-sm" />
          }
        </button>
      )}
    </div>
  );
}

export default function SessionsPage({ embedded = false }) {
  usePageTitle('Sessions');
  const { t } = useTranslation();
  const user = useSelector((state) => state.user);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revoking, setRevoking] = useState(null);
  const [revokingAll, setRevokingAll] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await getSessions();
      setSessions(data);
    } catch {
      toast.error(t('Failed to load sessions'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const handleRevoke = async (sessionId) => {
    setRevoking(sessionId);
    try {
      await revokeSession(sessionId);
      setSessions(prev => prev.filter(s => s._id !== sessionId));
      toast.success(t('Session revoked'));
    } catch {
      toast.error(t('Failed to revoke session'));
    } finally {
      setRevoking(null);
    }
  };

  const handleRevokeAll = async () => {
    setRevokingAll(true);
    try {
      await revokeOtherSessions();
      setSessions(prev => prev.filter(s => s.isCurrent));
      toast.success(t('All other sessions signed out'));
    } catch {
      toast.error(t('Failed to sign out other sessions'));
    } finally {
      setRevokingAll(false);
    }
  };

  const otherSessions = sessions.filter(s => !s.isCurrent);

  const content = (
    <div className="tw-flex tw-flex-col tw-gap-6">

        {/* Header row */}
        <div className="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-flex-wrap">
          <div>
            <h2 className="tw-text-white tw-text-lg tw-font-bold tw-m-0">{t("Active Sessions")}</h2>
            <p className="tw-text-white/40 tw-text-sm tw-mt-1 tw-m-0">
              {t("Devices currently signed in to your account.")}
            </p>
          </div>
          {otherSessions.length > 0 && (
            <button
              onClick={handleRevokeAll}
              disabled={revokingAll}
              className="tw-flex tw-items-center tw-gap-2 tw-rounded-full tw-border tw-border-red-500/40 tw-bg-red-500/10 tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-red-400 hover:tw-bg-red-500/20 tw-transition-all tw-cursor-pointer disabled:tw-opacity-50"
            >
              {revokingAll
                ? <FontAwesomeIcon icon={faRotateRight} className="tw-animate-spin" />
                : <FontAwesomeIcon icon={faShield} />
              }
              {t("Sign out all other devices")}
            </button>
          )}
        </div>

        {/* Sessions list */}
        <div className="tw-flex tw-flex-col tw-gap-3">
          {loading ? (
            <div className="tw-flex tw-justify-center tw-py-16">
              <FontAwesomeIcon icon={faRotateRight} className="tw-animate-spin tw-text-2xl tw-text-[#712CB0]" />
            </div>
          ) : sessions.length === 0 ? (
            <div className="tw-flex tw-flex-col tw-items-center tw-py-16 tw-gap-3 tw-text-white/30">
              <FontAwesomeIcon icon={faShield} className="tw-text-4xl" />
              <p className="tw-m-0 tw-text-sm">{t("No active sessions found")}</p>
            </div>
          ) : (
            <>
              {/* Current session first */}
              {sessions.filter(s => s.isCurrent).map(s => (
                <SessionCard key={s._id} session={s} onRevoke={handleRevoke} revoking={revoking} />
              ))}
              {/* Other sessions */}
              {otherSessions.length > 0 && (
                <>
                  <p className="tw-text-xs tw-font-semibold tw-text-white/30 tw-uppercase tw-tracking-widest tw-m-0 tw-mt-2">
                    {t("Other devices")}
                  </p>
                  {otherSessions.map(s => (
                    <SessionCard key={s._id} session={s} onRevoke={handleRevoke} revoking={revoking} />
                  ))}
                </>
              )}
            </>
          )}
        </div>

    </div>
  );

  if (embedded) return content;

  return (
    <DashboardShell role={user.role} pageTitle={t("Sessions")}>
      {content}
    </DashboardShell>
  );
}
